import CalculateIcon from "@mui/icons-material/Calculate";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { pink } from "@mui/material/colors";
import { useMemo, type FunctionComponent, type ReactNode } from "react";
import type DifferentiationMode from "../core/DifferentiationMode";
import type ExplainDerivativeData from "../features/ExplainDerivativeData";
import type ExplainDerivativeItem from "../features/ExplainDerivativeItem";
import { isSymbolicExplainDerivativeItem } from "../features/ExplainDerivativeController";
import type ManualDerivativePlan from "../features/ManualDerivativePlan";
import Katex from "../latex/Katex";
import MathLabel from "./MathLabel";

interface ManualDerivativePanelProps {
  plan: ManualDerivativePlan;
  computedNodeIds: ReadonlySet<string>;
  readyNodeIds: ReadonlySet<string>;
  selectedNodeId: string | null;
  // The explanation of the selected node, null when nothing is selected
  explainDerivativeData: ExplainDerivativeData | null;
  // Names of the nodes the selected one is still waiting for, empty when it's
  // ready or nothing is selected
  blockingNodeNames: string[];
  differentiationMode: DifferentiationMode;
  // Whether the values worked out so far are on show. Off, the run still
  // records them: only the reading is held back, so the user can go over the
  // same graph again without giving the progress up
  areAnswersShown: boolean;
  onCompute: () => void;
  onReset: () => void;
  onSelectNode: (nodeId: string) => void;
}

/**
 * The manual walkthrough, one tab of the derivatives inspector.
 *
 * The step tab drives the graph: press Next and the topological order decides
 * what happens. Here the graph drives the panel: the user clicks a node on the
 * canvas and the panel answers, either with the chain rule laid out for that
 * node or with the reason the pick was too early.
 *
 * The result of the chain rule is held back until the user asks for it. The
 * whole point of doing this by hand is the moment between seeing the numbers
 * and seeing what they add up to.
 */
const ManualDerivativePanel: FunctionComponent<ManualDerivativePanelProps> = ({
  plan,
  computedNodeIds,
  readyNodeIds,
  selectedNodeId,
  explainDerivativeData,
  blockingNodeNames,
  differentiationMode,
  areAnswersShown,
  onCompute,
  onReset,
  onSelectNode,
}) => {
  const totalNodes = plan.nodes.length;
  const computedCount = computedNodeIds.size;
  const isFinished = totalNodes > 0 && computedCount >= totalNodes;
  const isSelectedComputed =
    selectedNodeId !== null && computedNodeIds.has(selectedNodeId);
  const isSelectedReady =
    selectedNodeId !== null && readyNodeIds.has(selectedNodeId);
  // The header offers the action exactly when the body would have something to
  // reveal: a node that is picked, allowed, and not worked out yet
  const canCompute = isSelectedReady && !isSelectedComputed;

  /**
   * The explanation split into the three parts the panel puts in a different
   * order than the explanation panel does.
   *
   * Every shape the builder produces opens by naming the derivative it is
   * about and closes with the value: a chain rule sum, a 1 because f is x, or
   * a 0. So the first item is the subject, the last is the answer, and what
   * sits between them is the working.
   *
   * The subject stays on top and the answer follows it, which is what makes
   * the answer read as an answer: "Calculate the partial derivative: dE/dy"
   * then "Which is equal to: -0.578". Put the value first and its own
   * description points at something that has not been said yet.
   *
   * With the answers hidden the working is cut back to the items that are pure
   * symbols. Holding back only the last item is not enough: the substituted
   * chain rule of a node with one neighbor reads `-0.578 * 1`, which is the
   * answer in all but name.
   */
  const [subjectItem, workingItems, answerItem] = useMemo((): [
    ExplainDerivativeItem | null,
    ExplainDerivativeItem[],
    ExplainDerivativeItem | null,
  ] => {
    const items = explainDerivativeData?.items ?? [];
    if (items.length === 0) {
      return [null, [], null];
    }

    const hasSubject = items[0].type === "calculatePartialDerivative";
    const working = items.slice(hasSubject ? 1 : 0, -1);
    return [
      hasSubject ? items[0] : null,
      areAnswersShown
        ? working
        : working.filter((item) => isSymbolicExplainDerivativeItem(item.type)),
      items[items.length - 1],
    ];
  }, [areAnswersShown, explainDerivativeData]);

  const renderItem = (item: ExplainDerivativeItem): ReactNode => (
    <Stack key={item.type} spacing={0.5}>
      <MathLabel
        parts={item.descriptionParts}
        onClickLatexLink={onSelectNode}
      />
      {/* A fraction of a fraction can outgrow the column, so each row carries
          its own horizontal scroll rather than widening the dock */}
      <Box overflow="auto hidden" py={0.5}>
        <Katex latex={item.latex} />
      </Box>
    </Stack>
  );

  const body = useMemo((): ReactNode => {
    if (plan.targetNodeId === null) {
      return (
        <Alert severity="info" variant="outlined">
          <Typography variant="caption">
            Pick a derivative target first. Without one there&apos;s no chain
            rule to start from.
          </Typography>
        </Alert>
      );
    }

    if (totalNodes === 0) {
      return (
        <Alert severity="info" variant="outlined">
          <Typography variant="caption">
            Nothing to work out: no node other than the target is on the
            differentiation path.
          </Typography>
        </Alert>
      );
    }

    if (selectedNodeId === null) {
      return (
        <Typography variant="caption" color="text.secondary">
          {isFinished
            ? "Every derivative is worked out. Click a node to look at how it was reached, or reset to start over."
            : `Click one of the ${readyNodeIds.size} highlighted node(s) on the canvas. They're the ones whose neighbors you've already worked out.`}
        </Typography>
      );
    }

    if (!isSelectedReady && !isSelectedComputed) {
      return (
        <Alert severity="warning" variant="outlined">
          <Typography variant="caption">
            {differentiationMode === "REVERSE"
              ? "Its chain rule needs the derivatives of its output nodes, and these aren't worked out yet: "
              : "Its chain rule needs the derivatives of its input nodes, and these aren't worked out yet: "}
          </Typography>
          {blockingNodeNames.map((nodeName, index) => (
            <Box key={nodeName} display="inline">
              {index > 0 && (
                <Typography variant="caption" display="inline">
                  ,{" "}
                </Typography>
              )}
              <Katex latex={nodeName} />
            </Box>
          ))}
        </Alert>
      );
    }

    return (
      <Stack spacing={1} divider={<Divider flexItem />}>
        {/* Which derivative this is about, which is what the answer under it
        refers to */}
        {subjectItem !== null && renderItem(subjectItem)}

        {/* Says why the working stops at symbols, so that a stripped panel
        doesn't read as a missing one */}
        {!areAnswersShown && (
          <Typography variant="caption" color="text.secondary">
            {isSelectedComputed
              ? "Worked out. Its value, and the ones it came from, are hidden by the Show answers setting."
              : "The Show answers setting is off, so the chain rule is shown in symbols only."}
          </Typography>
        )}

        {/* The answer sits above the working rather than after it: a chain rule
        with several terms would otherwise drop the result somewhere the user
        has to go looking for */}
        {isSelectedComputed &&
          areAnswersShown &&
          answerItem !== null &&
          renderItem(answerItem)}

        {workingItems.map(renderItem)}
      </Stack>
    );
    // renderItem is a plain render helper rather than a dependency: it only
    // closes over onSelectNode, which is already listed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    answerItem,
    areAnswersShown,
    blockingNodeNames,
    differentiationMode,
    isFinished,
    isSelectedComputed,
    isSelectedReady,
    onSelectNode,
    plan.targetNodeId,
    readyNodeIds.size,
    selectedNodeId,
    subjectItem,
    totalNodes,
    workingItems,
  ]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      // Without this the working refuses to shrink below its content and
      // pushes the column past the bottom of the canvas instead of scrolling
      sx={{ minHeight: 0 }}
    >
      {/* Header. Pinned outside the scroll area so that Exit and the progress
          stay reachable however long the working below gets */}
      <Stack
        px={1}
        py={0.75}
        spacing={0.5}
        borderBottom={1}
        borderColor="divider"
        // The working below scrolls; the controls stay put however long it runs
        flexShrink={0}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Chip
            size="small"
            label="manual"
            sx={{
              fontWeight: 600,
              color: "common.white",
              backgroundColor: pink[600],
            }}
          />

          {/* The one thing the user came here to press, in the row that never
          scrolls away */}
          {canCompute && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CalculateIcon />}
              onClick={onCompute}
              aria-label="compute-manual-derivative"
            >
              Compute
            </Button>
          )}

          <Box flexGrow={1} display="flex" justifyContent="flex-end">
            <Tooltip title="Start over">
              {/* The span lets the tooltip work while the
                  button is disabled */}
              <Box component="span" display="flex">
                <IconButton
                  size="small"
                  disabled={computedCount === 0}
                  onClick={onReset}
                  aria-label="reset-manual-derivative"
                >
                  <ReplayIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        </Stack>

        {/* Target and progress. The target shares this row rather than the one
        above so that the button up there keeps its label at the narrow end of
        the column */}
        <Stack direction="row" alignItems="center" spacing={1}>
          {plan.targetNodeName !== null && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              overflow="hidden"
            >
              <Typography variant="body2" color="text.secondary" noWrap>
                target:
              </Typography>
              <Katex latex={plan.targetNodeName} />
            </Stack>
          )}
          <LinearProgress
            variant="determinate"
            color="secondary"
            value={totalNodes === 0 ? 0 : (computedCount / totalNodes) * 100}
            sx={{ height: 4, borderRadius: 2, flexGrow: 1 }}
          />
          <Typography variant="caption" color="text.secondary">
            {`${computedCount} / ${totalNodes}`}
          </Typography>
        </Stack>
      </Stack>

      {/* Working */}
      <Box flexGrow={1} overflow="auto" px={1.5} py={1} sx={{ minHeight: 0 }}>
        <Stack spacing={1}>
          {/* Selected node */}
          {explainDerivativeData !== null && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="subtitle2">Node:&nbsp;</Typography>
              <Katex latex={explainDerivativeData.nodeName} />
            </Stack>
          )}

          {body}
        </Stack>
      </Box>
    </Box>
  );
};

export default ManualDerivativePanel;
